import {
  get,
  find,
  filter,
  reject,
  maxBy,
  minBy,
  capitalize,
  orderBy,
} from 'lodash';
import { format as d3Format } from 'd3-format';
import fractionText from './fraction-text';

export default (__DATA__, __CONTEXT__, __DATUM__) => {
  // If there's a geo comparison active, make all the defaults the default geo's
  // data.
  const geoData = __CONTEXT__.geo_code_cmp
    ? filter(__DATA__, ['geo_code_long', __CONTEXT__.geo_code])
    : __DATA__;
  const defaultData = geoData.length ? geoData : __DATA__;

  return {
    __DATA__,
    __CONTEXT__,
    __DATUM__: __DATUM__ || {},

    // Filter:
    filterBy: (field, value, data = defaultData) =>
      filter(data, [field, value]),
    filterByNot: (field, value, data = defaultData) =>
      reject(data, [field, value]),
    filterByGt: (field, value, data = defaultData) =>
      filter(data, (d) => Number(d[field]) > value),
    filterByGte: (field, value, data = defaultData) =>
      filter(data, (d) => Number(d[field]) >= value),
    filterByLt: (field, value, data = defaultData) =>
      filter(data, (d) => Number(d[field]) < value),
    filterByLte: (field, value, data = defaultData) =>
      filter(data, (d) => Number(d[field]) <= value),
    filterByMin: (field = __CONTEXT__.value_field, data = defaultData) =>
      filter(data, [field, minBy(data, field)]),
    filterByMax: (field = __CONTEXT__.value_field, data = defaultData) =>
      filter(data, [field, maxBy(data, field)]),

    // Find:
    findBy: (field, value, data = defaultData) => find(data, [field, value]),
    // find(data, [field, value]),
    findByNot: (field, value, data = defaultData) =>
      find(data, (d) => d[field] !== value),
    findByGt: (field, value, data = defaultData) =>
      find(data, (d) => Number(d[field]) > value),
    findByGte: (field, value, data = defaultData) =>
      find(data, (d) => Number(d[field]) >= value),
    findByLt: (field, value, data = defaultData) =>
      find(data, (d) => Number(d[field]) < value),
    findByLte: (field, value, data = defaultData) =>
      find(data, (d) => Number(d[field]) <= value),
    findByMax: (field = __CONTEXT__.value_field, data = defaultData) =>
      maxBy(data, field),
    findByMin: (field = __CONTEXT__.value_field, data = defaultData) =>
      minBy(data, field),

    // Accessors:
    get: (obj, field = __CONTEXT__.value_field, forceRaw = false) =>
      (!forceRaw && get(obj, `${field}_label`)) || get(obj, field),
    mouseover: (field = __CONTEXT__.value_field, forceRaw = false) =>
      (!forceRaw && get(__DATUM__, `${field}_label`)) || get(__DATUM__, field),
    context: (field) => get(__CONTEXT__, field),
    key: (obj, field = __CONTEXT__.value_field) => get(obj, field),
    rank: (datum) =>
      orderBy(__DATA__, __CONTEXT__.value_field, 'desc').indexOf(datum) + 1,

    // Format:
    format: (format, value, pos, neg, symbolPosition = 'before') => {
      const dropPercent = format.endsWith('%-');
      const spec = format.replace(/%-$/, '%');
      const n =
        pos !== undefined || neg !== undefined ? Math.abs(value) : value;
      const parts = [d3Format(spec)(n).replace(/G$/, 'B').replace(/k$/, 'K')];
      const symbol = n === value ? pos : neg;
      const method = symbolPosition === 'before' ? 'unshift' : 'push';

      if (symbol) parts[method](symbol);

      const out = filter(parts).join('');

      return dropPercent ? out.replace(/%$/, '') : out;
    },
    percent: (value, decimals = 0) => d3Format(`.${Number(decimals)}%`)(value),
    decimal: (value, decimals = 2) => d3Format(`.${Number(decimals)}f`)(value),
    integer: (value) => d3Format(',d')(value),
    lowercase: (str) => String(str).toLowerCase(),
    capitalize,
    fractionText,

    // Sum:
    count: (arr = defaultData) => arr.length,
    sumBy: (field = __CONTEXT__.value_field, data = defaultData) =>
      data.reduce((acc, x) => acc + Number(x[field]), 0),

    // Logic:
    if: (condition, pass, fail = '') => (condition ? pass : fail),
  };
};
